/**
 * Primitives shared by every persisted document schema.
 *
 * ## Two rules the whole document model obeys
 *
 * 1. **`null`, never `undefined`.** Every optional value in a persisted
 *    document is spelled `T | null` and is always present as a key. A
 *    `JSON.parse(JSON.stringify(doc))` round-trip must be `toEqual`-identical
 *    to the original, and an absent key is not (it vanishes). The `| null`
 *    style also plays correctly with the repo's `exactOptionalPropertyTypes`.
 *    Conversion to `@beerwright/core`'s `?:`-style optionals happens in
 *    `../bridge/calculation.ts`, at the edge, and nowhere else.
 *
 * 2. **No wall-clock inside.** Nothing in this package calls `Date.now()`,
 *    `Math.random()` or `crypto.randomUUID()`. Timestamps arrive on the command
 *    envelope and ids arrive in command payloads, which is what makes
 *    `applyLog` deterministic (PLAN §10, "same log ⇒ same state").
 *
 * Units follow the `@beerwright/core` canon throughout: kg, L, °C, minutes/days,
 * SG, °L, ppm, percentages 0–100 (PLAN §5).
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------

/**
 * The character set a document/item id may use.
 *
 * Deliberately permissive — the client mints uuids, the AI assistant mints
 * readable slugs, and BeerXML importers reuse foreign keys — but it excludes
 * whitespace and control characters so ids stay safe in URLs, log lines and
 * `Record` keys.
 */
export const DOCUMENT_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,63}$/;

/**
 * An opaque, caller-supplied identifier.
 *
 * This package never generates one: the reducer is a pure function of
 * `(state, command log)`, so every id has to arrive in a payload.
 */
export const documentIdSchema = z
  .string()
  .regex(DOCUMENT_ID_PATTERN, 'must be 1–64 chars of [A-Za-z0-9._:-] starting alphanumeric');

/**
 * A link from an item to one of the brewer's own ingredients: the id of a
 * `CustomIngredientDoc`, or `null` for an item that is not linked.
 *
 * ## Required in the output, forgiving of absence in the input
 *
 * Every lot and every recipe row carries this key once parsed (`string | null`,
 * never absent), which is what lets the matcher and the reducer read it without
 * a guard. But the retained command log and every IndexedDB snapshot were
 * written before the key existed, so the parse has to accept its absence — and
 * `.default(null)` is not the way to do that here. A default survives
 * `.partial()`, and the change-set schemas are built by `.omit().partial()` on
 * these very documents: a defaulted key would ride along as `ingredientId:
 * null` on every patch that omitted it and quietly unlink the item being
 * edited. The `water` section documents the same rule at `kettleTargetPh`.
 *
 * So the fallback lives *inside the field*: a preprocess that turns `undefined`
 * into `null` before the id check runs. Under `.partial()` the field is wrapped
 * in an optional whose short-circuit fires first, so an absent key stays absent
 * — verified against Zod 4.4.3, and pinned by `ingredient-link.test.ts`.
 *
 * ## Not a `catalogRef`
 *
 * `catalogRef` is provenance into an immutable package; this is a foreign key
 * into workspace data that syncs, undoes and can be deleted. An item carries at
 * most one of the two — see {@link hasOneProvenance}.
 */
export const ingredientLinkSchema = z.preprocess(
  (value) => (value === undefined ? null : value),
  documentIdSchema.nullable(),
);

/**
 * At most one of `catalogRef` and `ingredientId` is set.
 *
 * A catalog-backed row snapshots its display fields and keeps them; a linked
 * row lets the library own them. A row claiming both would have two owners for
 * its name and no rule for which wins, so the schema refuses it up front. Used
 * as the `.refine()` predicate on every item document, and re-checked by the
 * update reducers on the merged row, because a patch can set one key on a row
 * that already has the other.
 */
export function hasOneProvenance(item: {
  readonly catalogRef: unknown;
  readonly ingredientId: string | null;
}): boolean {
  return item.catalogRef === null || item.ingredientId === null;
}

/**
 * The refinement issue {@link hasOneProvenance} reports, pointed at the link.
 *
 * Annotated rather than `as const`: Zod's `.refine()` takes `path?: PropertyKey[]`,
 * and a `readonly ['ingredientId']` tuple is not assignable to a mutable array,
 * so the const-assertion form cannot be handed to the four item schemas that use
 * it. The annotation keeps the value shared in one place, which is the point —
 * every list reports the same sentence at the same path.
 */
export const ONE_PROVENANCE_ISSUE: { readonly message: string; readonly path: PropertyKey[] } = {
  message:
    'an item is catalog-backed or linked to one of your ingredients, never both: set ingredientId only when catalogRef is null',
  path: ['ingredientId'],
};

// ---------------------------------------------------------------------------
// Scalars
// ---------------------------------------------------------------------------

/** Milliseconds since the Unix epoch. Supplied by the caller, never read from a clock. */
export const timestampSchema = z.number().int().min(0);

/** A finite number ≥ 0. */
export const nonNegativeSchema = z.number().finite().min(0);

/** A finite number > 0. */
export const positiveSchema = z.number().finite().gt(0);

/** A percentage expressed 0–100, matching core's convention. */
export const percentSchema = z.number().finite().min(0).max(100);

/** A ratio expressed 0–1. */
export const fractionSchema = z.number().finite().min(0).max(1);

/**
 * A specific gravity.
 *
 * Bounded generously rather than tightly: 0.98 admits a fully-attenuated saison
 * that finished below 1.000, and 1.200 admits an eisbock concentrate. A reading
 * outside that is a typo, and refusing it at the schema is kinder than
 * propagating a NaN-shaped ABV through the actuals table.
 *
 * Here rather than in `batch.ts`, where it was first written, because the
 * recipe now states a target gravity as well as the batch recording a measured
 * one — and `batch.ts` imports the recipe document, so the recipe cannot import
 * back. A target and a measurement are the same physical quantity, and two
 * definitions of what counts as a gravity is how they drift apart.
 */
export const specificGravitySchema = z.number().finite().min(0.98).max(1.2);

/** A human-facing display name. */
/**
 * Longest a user-typed name may be.
 *
 * Exported because the UI validates against it too — `NameDialog` refuses a
 * name the command would refuse, under the field rather than as a toast after
 * the fact — and two copies of 120 would eventually be two different numbers.
 */
export const NAME_MAX_LENGTH = 120;

export const nameSchema = z.string().min(1).max(NAME_MAX_LENGTH);

/** Free-form user prose. Bounded so a runaway paste cannot blow the sync payload. */
export const notesSchema = z.string().max(20_000);

/** User-defined labels for filtering the recipe list. */
export const tagsSchema = z.array(z.string().min(1).max(40)).max(32);

/** An ISO-8601 date, `YYYY-MM-DD`. Dates are stored as strings so documents stay JSON. */
export const isoDateSchema = z.iso.date();

/**
 * An ISO 4217 alphabetic code (`USD`, `EUR`, …).
 *
 * Display only: stored lot `unitCost`s are dimensionless numbers in whatever
 * currency the workspace currently names. Changing the code does **not**
 * convert them.
 */
export const currencyCodeSchema = z
  .string()
  .regex(/^[A-Z]{3}$/, 'must be a 3-letter ISO 4217 currency code');

// ---------------------------------------------------------------------------
// Catalog provenance
// ---------------------------------------------------------------------------

/** The catalog collections a recipe ingredient can be snapshotted from. */
export const catalogRefKindSchema = z.enum(['fermentable', 'hop', 'yeast', 'misc']);

/** @see catalogRefKindSchema */
export type CatalogRefKind = z.infer<typeof catalogRefKindSchema>;

/**
 * Where a snapshotted ingredient came from (PLAN §6).
 *
 * The reference is **provenance, not a foreign key**: nothing in the engine
 * ever follows it to re-read a value. Every number the maths needs is copied
 * into the recipe item at add-time, so updating the catalog can never move an
 * old recipe's vitals. The reference exists so the UI can say "this is Citra,
 * snapshotted at catalog 2026.08.0" and offer a *deliberate* refresh.
 *
 * `null` on an item means a fully custom ingredient with no catalog origin.
 */
export const catalogRefSchema = z.strictObject({
  /** Which collection the entry lives in. */
  kind: catalogRefKindSchema,
  /** The catalog entry's stable slug, e.g. `citra`. */
  id: z.string().min(1).max(80),
  /** `CATALOG_VERSION` at the moment the snapshot was taken, e.g. `2026.08.0`. */
  catalogVersion: z.string().min(1).max(24),
});

/** @see catalogRefSchema */
export type CatalogRef = z.infer<typeof catalogRefSchema>;

// ---------------------------------------------------------------------------
// Core enum mirrors
// ---------------------------------------------------------------------------

// These restate `@beerwright/core`'s string unions as Zod enums. The values are
// asserted equal to core's at type level in `document/enums.test.ts`, so a
// change in core that this package has not followed fails `pnpm typecheck`
// rather than silently accepting a value the engine will reject.

/**
 * Mirrors core's `HopStoragePlace`. Where a hop lot is kept. These ids are
 * stored on lots and in settings, and the web hop age dialog uses them too.
 */
export const hopStoragePlaceSchema = z.enum(['freezer', 'fridge', 'cellar', 'room']);

/** Mirrors core's `HopPackaging`. What a hop lot is kept in. */
export const hopPackagingSchema = z.enum(['nitrogen-flushed', 'vacuum', 'wrapped', 'loose']);

/** Mirrors core's `FermentableClass`. */
export const fermentableClassSchema = z.enum([
  'grain',
  'steeping-grain',
  'dry-extract',
  'liquid-extract',
  'sugar',
  'non-fermentable',
]);

/** Mirrors core's `FermentableUse`. */
export const fermentableUseSchema = z.enum(['mash', 'steep', 'boil', 'whirlpool', 'fermenter']);

/**
 * Mirrors core's `MaltPhClass` and has to track its list. Core's color solver
 * also reads these names: `crystal` and `roasted` are the malts it adjusts.
 */
export const maltPhClassSchema = z.enum([
  'base',
  'munich-vienna',
  'wheat-other',
  'crystal',
  'roasted',
  'acidulated',
  'none',
]);

/** Mirrors core's `HopForm`. */
export const hopFormSchema = z.enum([
  'whole',
  'plug',
  'pellet',
  'cryo',
  'co2-extract',
  'iso-extract',
  'aroma-extract',
]);

/** Mirrors core's `HopUse`. */
export const hopUseSchema = z.enum(['first-wort', 'boil', 'whirlpool', 'dry-hop', 'keg']);

/** Mirrors core's `YeastType`. */
export const yeastTypeSchema = z.enum(['ale', 'lager', 'wheat', 'wine', 'brett', 'other']);

/** Mirrors core's `YeastForm`. */
export const yeastFormSchema = z.enum(['dry', 'liquid']);

/** Mirrors core's `MashStepType`. */
export const mashStepTypeSchema = z.enum(['infusion', 'temperature', 'decoction', 'sparge']);

/** Mirrors core's `DecoctionThickness`. */
export const decoctionThicknessSchema = z.enum(['thick', 'medium', 'thin']);

/** Mirrors core's `FermentationStepType`. */
export const fermentationStepTypeSchema = z.enum([
  'primary',
  'secondary',
  'conditioning',
  'lagering',
]);

/** Mirrors core's `SpargeType`. */
export const spargeTypeSchema = z.enum(['batch', 'fly', 'no-sparge', 'biab-full-volume']);

/** Mirrors core's `BatchVolumeBasis`. */
export const batchVolumeBasisSchema = z.enum(['fermenter', 'packaged']);

/**
 * How the beer leaves the fermenter.
 *
 * `bottle` and `keg-conditioning` prime with the same sugar at the same dose —
 * the folk reduction for kegs is argued down on core's `primingSugar` — and
 * differ only in what the packaging panel prints. `keg` is forced carbonation,
 * where the answer is a regulator setting and there is no sugar at all.
 */
export const packagingMethodSchema = z.enum(['bottle', 'keg', 'keg-conditioning']);

/** @see packagingMethodSchema */
export type PackagingMethod = z.infer<typeof packagingMethodSchema>;

/** Human labels, so a command summary and the UI cannot word it differently. */
export const PACKAGING_METHOD_LABELS: Readonly<Record<PackagingMethod, string>> = {
  bottle: 'Bottle',
  keg: 'Keg',
  'keg-conditioning': 'Keg conditioning',
};

/** Mirrors core's `PrimingSugar`. */
export const primingSugarSchema = z.enum([
  'corn-sugar',
  'dextrose',
  'table-sugar',
  'dme',
  'honey',
  'turbinado',
  'maple-syrup',
]);

/** @see primingSugarSchema */
export type PrimingSugarId = z.infer<typeof primingSugarSchema>;

/** Mirrors core's `SaltId`. */
export const saltIdSchema = z.enum([
  'gypsum',
  'calcium-chloride-dihydrate',
  'calcium-chloride-anhydrous',
  'epsom-salt',
  'table-salt',
  'magnesium-chloride',
  'baking-soda',
  'chalk',
  'slaked-lime',
]);

/** Mirrors core's `AcidId`. */
export const acidIdSchema = z.enum([
  'lactic',
  'phosphoric',
  'citric',
  'acetic',
  'hydrochloric',
  'sulfuric',
]);

/**
 * The acids a brewer can stock, as bottle-and-strength rather than as acid.
 *
 * `AcidId` says lactic; a cupboard says "an 88 % bottle of lactic", which is
 * what the shop sells and what the dose is worked out against. So the picker's
 * own entry ids are what {@link workspaceSettingsSchema}'s `stockedAcids`
 * stores, and the web app's `ACID_CHOICES` types its `value` against this enum
 * — a strength added there without being added here is a compile error.
 *
 * Acidulated malt is deliberately absent: it goes in the grist, not the water,
 * so it is not a bottle and the cupboard never hides it.
 */
export const acidChoiceIdSchema = z.enum([
  'lactic-88',
  'lactic-80',
  'phosphoric-85',
  'phosphoric-75',
  'phosphoric-10',
  'citric-solid',
  'acetic-5',
  'hydrochloric-31',
  'sulfuric-35',
]);

/** @see acidChoiceIdSchema */
export type AcidChoiceId = z.infer<typeof acidChoiceIdSchema>;

/**
 * The cupboard a new workspace starts with: everything but the strong mineral
 * acids. Hydrochloric 31 % and sulfuric 35 % are offered because the chemistry
 * is sound and a few brewers do use them, but they are not what a homebrew
 * shop hands you, so they start off and get turned on by the brewer who has
 * them — the same rule the salt cupboard writes.
 */
export const DEFAULT_STOCKED_ACIDS: readonly AcidChoiceId[] = [
  'lactic-88',
  'lactic-80',
  'phosphoric-85',
  'phosphoric-75',
  'phosphoric-10',
  'citric-solid',
  'acetic-5',
];

/** Mirrors core's `WaterTreatmentTarget`. */
export const waterTreatmentTargetSchema = z.enum(['mash', 'sparge', 'total']);

/** Mirrors the catalog's `MiscType`. */
export const miscTypeSchema = z.enum([
  'water-agent',
  'acid',
  'alkali',
  'fining',
  'nutrient',
  'water-treatment',
  'enzyme',
  'spice',
  'hop-flavour',
  'other',
]);

/** Mirrors the catalog's `MiscUse`. */
export const miscUseSchema = z.enum([
  'water',
  'mash',
  'sparge',
  'boil',
  'whirlpool',
  'primary',
  'secondary',
  'packaging',
]);

/** Mirrors the catalog's `MiscForm`. */
export const miscFormSchema = z.enum(['powder', 'granular', 'liquid', 'tablet', 'solid']);

/** @see miscFormSchema */
export type MiscForm = z.infer<typeof miscFormSchema>;

/**
 * How a misc addition is measured.
 *
 * Deliberately tiny: brewing miscs are weighed (`g`), pipetted (`ml`) or
 * counted (`each`, e.g. Whirlfloc tablets). Everything else is a display
 * concern the unit preferences handle.
 */
export const miscAmountUnitSchema = z.enum(['g', 'ml', 'each']);

/** @see miscAmountUnitSchema */
export type MiscAmountUnit = z.infer<typeof miscAmountUnitSchema>;
